import { int32, struct, uint32 } from "@stellar/js-xdr";
import type { XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { XdrString, xdrString } from "../values/xdr-string.js";
import { Hash, type HashWire } from "./hash.js";
import { PublicKey, type PublicKeyWire } from "./public-key.js";
import { AuthCert, type AuthCertWire } from "./auth-cert.js";
import { Uint256Bytes, type Uint256BytesWire } from "./uint256-bytes.js";

export interface HelloWire {
  ledgerVersion: number;
  overlayVersion: number;
  overlayMinVersion: number;
  networkId: HashWire;
  versionStr: XdrString;
  listeningPort: number;
  peerId: PublicKeyWire;
  cert: AuthCertWire;
  nonce: Uint256BytesWire;
}

/**
 * ```xdr
 * struct Hello
 * {
 *     uint32 ledgerVersion;
 *     uint32 overlayVersion;
 *     uint32 overlayMinVersion;
 *     Hash networkID;
 *     string versionStr<100>;
 *     int listeningPort;
 *     NodeID peerID;
 *     AuthCert cert;
 *     uint256 nonce;
 * };
 * ```
 */
export class Hello extends XdrValue {
  readonly ledgerVersion: number;
  readonly overlayVersion: number;
  readonly overlayMinVersion: number;
  readonly networkId: Hash;
  readonly versionStr: XdrString;
  readonly listeningPort: number;
  readonly peerId: PublicKey;
  readonly cert: AuthCert;
  readonly nonce: Uint256Bytes;

  static readonly schema: XdrType<HelloWire> = struct("Hello", {
    ledgerVersion: uint32(),
    overlayVersion: uint32(),
    overlayMinVersion: uint32(),
    networkId: Hash.schema,
    versionStr: xdrString(100),
    listeningPort: int32(),
    peerId: PublicKey.schema,
    cert: AuthCert.schema,
    nonce: Uint256Bytes.schema,
  });

  constructor(input: {
    ledgerVersion: number;
    overlayVersion: number;
    overlayMinVersion: number;
    networkId: Hash | Uint8Array | string;
    versionStr: XdrString | string | Uint8Array;
    listeningPort: number;
    peerId: PublicKey;
    cert: AuthCert;
    nonce: Uint256Bytes | Uint8Array | string;
  }) {
    super();
    this.ledgerVersion = input.ledgerVersion;
    this.overlayVersion = input.overlayVersion;
    this.overlayMinVersion = input.overlayMinVersion;
    this.networkId =
      input.networkId instanceof Hash
        ? input.networkId
        : new Hash(input.networkId);
    this.versionStr =
      input.versionStr instanceof XdrString
        ? input.versionStr
        : new XdrString(input.versionStr);
    this.listeningPort = input.listeningPort;
    this.peerId = input.peerId;
    this.cert = input.cert;
    this.nonce =
      input.nonce instanceof Uint256Bytes
        ? input.nonce
        : new Uint256Bytes(input.nonce);
  }

  toXdrObject(): HelloWire {
    return {
      ledgerVersion: this.ledgerVersion,
      overlayVersion: this.overlayVersion,
      overlayMinVersion: this.overlayMinVersion,
      networkId: this.networkId.toXdrObject(),
      versionStr: this.versionStr,
      listeningPort: this.listeningPort,
      peerId: this.peerId.toXdrObject(),
      cert: this.cert.toXdrObject(),
      nonce: this.nonce.toXdrObject(),
    };
  }

  static fromXdrObject(wire: HelloWire): Hello {
    return new Hello({
      ledgerVersion: wire.ledgerVersion,
      overlayVersion: wire.overlayVersion,
      overlayMinVersion: wire.overlayMinVersion,
      networkId: Hash.fromXdrObject(wire.networkId),
      versionStr: wire.versionStr,
      listeningPort: wire.listeningPort,
      peerId: PublicKey.fromXdrObject(wire.peerId),
      cert: AuthCert.fromXdrObject(wire.cert),
      nonce: Uint256Bytes.fromXdrObject(wire.nonce),
    });
  }
}
